import { useCallback } from 'react'
import {
  useController,
  useFormContext,
  useFormState,
  type FieldError,
  type FieldValues,
  type Path,
} from 'react-hook-form'
import { useIntl, type IntlShape } from 'react-intl'

import { Checkbox, Select, TextField, type SelectOption } from '@/shared/ui'

import { serverErrorType } from './server-errors'

/**
 * A field error's text: the API's, as pplyServerErrors put it, else the schema's message, a catalog id (as
 * `z.string().min(1, 'people.firstNameRequired')`), else a general one.
 */
export function fieldErrorText(intl: IntlShape, error: FieldError | undefined): string | undefined {
  const message = error?.message
  if (error === undefined) {
    return undefined
  }
  if (error.type === serverErrorType && message !== undefined && message !== '') {
    return message
  }
  return message !== undefined && Object.hasOwn(intl.messages, message)
    ? intl.formatMessage({ id: message })
    : intl.formatMessage({ id: 'forms.field.invalid' })
}

/** The error of one field of the surrounding form, ready to show. */
function useFieldError<V extends FieldValues>(name: Path<V>) {
  const intl = useIntl()
  const { getFieldState } = useFormContext<V>()
  const formState = useFormState<V>({ name })
  return fieldErrorText(intl, getFieldState(name, formState).error)
}

/**
 * A text field bound to the surrounding form (`RecordForm`'s) by `name`, showing its error. Put
 * it in a `FormRow`, which names it, or give it a `label`. In a row of several fields, give each
 * a `labelSuffix` so their names differ ("Duration value", "Duration unit").
 */
export function FormTextField<V extends FieldValues>(props: {
  readonly name: Path<V>
  readonly label?: string
  readonly labelSuffix?: string
  readonly type?: 'text' | 'email' | 'tel' | 'url' | 'password' | 'number'
  readonly placeholder?: string
  readonly autoComplete?: string
  readonly className?: string
  /** A text area of this many rows, for longer text such as a description. */
  readonly rows?: number
}) {
  const { name, rows, type, autoComplete, ...rest } = props
  const { register } = useFormContext<V>()
  const error = useFieldError<V>(name)
  const shared = { ...rest, ...register(name), ...(error === undefined ? {} : { error }) }
  return rows === undefined ? (
    <TextField {...shared} {...(type === undefined ? {} : { type })} {...(autoComplete === undefined ? {} : { autoComplete })} />
  ) : (
    <TextField {...shared} multiline rows={rows} {...(autoComplete === undefined ? {} : { autoComplete })} />
  )
}

/**
 * A select bound to the surrounding form by `name`. Put it in a `FormRow`, or give it a `label`;
 * in a row of several fields, give it a `labelSuffix`.
 */
export function FormSelect<V extends FieldValues, T extends string | number>(props: {
  readonly name: Path<V>
  readonly options: readonly (SelectOption<T> | (T & string))[]
  readonly label?: string
  readonly labelSuffix?: string
  readonly className?: string
}) {
  const intl = useIntl()
  const { field, fieldState } = useController<V>({ name: props.name })
  const error = fieldErrorText(intl, fieldState.error)
  return (
    <Select<T>
      {...(props.label === undefined ? {} : { label: props.label })}
      {...(props.labelSuffix === undefined ? {} : { labelSuffix: props.labelSuffix })}
      value={field.value as T}
      onChange={field.onChange}
      name={field.name}
      onBlur={field.onBlur}
      inputRef={field.ref}
      options={props.options}
      {...(error === undefined ? {} : { error })}
      {...(props.className === undefined ? {} : { className: props.className })}
    />
  )
}

/**
 * A checkbox bound to the surrounding form by `name`. Alone in a `FormRow`, the row names it; in
 * a row of several fields, give it a `labelSuffix`.
 */
export function FormCheckbox<V extends FieldValues>(props: {
  readonly name: Path<V>
  readonly label?: string
  readonly labelSuffix?: string
  readonly className?: string
}) {
  const intl = useIntl()
  const { field, fieldState } = useController<V>({ name: props.name })
  const error = fieldErrorText(intl, fieldState.error)
  const { onChange } = field
  const change = useCallback((checked: boolean) => onChange(checked), [onChange])
  return (
    <Checkbox
      checked={field.value === true}
      onChange={change}
      name={field.name}
      onBlur={field.onBlur}
      inputRef={field.ref}
      {...(props.label === undefined ? {} : { label: props.label })}
      {...(props.labelSuffix === undefined ? {} : { labelSuffix: props.labelSuffix })}
      {...(error === undefined ? {} : { error })}
      {...(props.className === undefined ? {} : { className: props.className })}
    />
  )
}
